import { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { CompetencyResult } from '../../types/api';

interface CompetencyRadarProps {
  competencies: (CompetencyResult | string)[];
}

export default function CompetencyRadar({ competencies }: CompetencyRadarProps) {
  const primaries = useMemo(
    () =>
      competencies
        .filter((c): c is CompetencyResult => typeof c === 'object' && !!c.is_primary)
        .map(c => ({
          keyword: c.keyword || '',
          importance: typeof c.importance === 'number' ? c.importance : 0,
        })),
    [competencies],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null);

  const sorted = useMemo(
    () => [...primaries].sort((a, b) => a.keyword.localeCompare(b.keyword)),
    [primaries],
  );
  const sortedRef = useRef(sorted);
  sortedRef.current = sorted;

  const enabled = primaries.length >= 3;

  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    const onMove = (e: MouseEvent) => {
      const data = sortedRef.current;
      if (data.length === 0) return;
      const r = el.getBoundingClientRect();
      const w = r.width;
      const h = r.height;
      const cx = w * 0.5;
      const cy = h * 0.52;
      const radius = Math.min(w, h) * 0.5 * 0.6;
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      const n = data.length;
      let bestIdx = -1;
      let bestDist = Infinity;
      let bestX = 0;
      let bestY = 0;
      for (let i = 0; i < n; i++) {
        const angle = ((90 + i * (360 / n)) * Math.PI) / 180;
        const vx = cx + Math.cos(angle) * radius * data[i].importance;
        const vy = cy - Math.sin(angle) * radius * data[i].importance;
        const d = Math.hypot(mx - vx, my - vy);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
          bestX = vx;
          bestY = vy;
        }
      }
      if (bestDist < 30) {
        setHover({ idx: bestIdx, x: bestX, y: bestY });
      } else {
        setHover(null);
      }
    };
    const onLeave = () => setHover(null);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [enabled]);

  const maxImp = useMemo(
    () => (primaries.length === 0 ? 0 : Math.max(...primaries.map(p => p.importance))),
    [primaries],
  );

  const option = useMemo(() => {
    const wrap = (s: string, max = 14): string => {
      const words = s.split(/\s+/);
      const lines: string[] = [''];
      let i = 0;
      for (const w of words) {
        const candidate = lines[i] ? `${lines[i]} ${w}` : w;
        if (candidate.length > max && lines[i]) {
          lines.push(w);
          i++;
        } else {
          lines[i] = candidate;
        }
      }
      return lines.join('\n');
    };
    const indicators = sorted.map(d => ({
      name: wrap(d.keyword, 14),
      max: 1,
      isMax: d.importance === maxImp,
    }));
    const values = sorted.map(d => d.importance);
    const strokeColor = '#475569'; // slate-600

    return {
      tooltip: { show: false },
      radar: {
        shape: 'polygon' as const,
        radius: '60%',
        center: ['50%', '52%'],
        startAngle: 90,
        splitNumber: 4,
        indicator: indicators,
        axisName: {
          color: '#475569',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 15,
          formatter: (name: string, ind: any) =>
            ind.isMax ? `{max|${ind.name}}` : `{normal|${ind.name}}`,
          rich: {
            max: { color: '#0F172A', fontWeight: 700, fontSize: 12, lineHeight: 16 },
            normal: { color: '#64748B', fontWeight: 500, fontSize: 12, lineHeight: 15 },
          },
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        splitLine: { lineStyle: { color: '#E2E8F0', width: 1 } },
        splitArea: {
          areaStyle: { color: ['#FFFFFF', '#F8FAFC'] },
        },
      },
      series: [
        {
          type: 'radar' as const,
          symbol: 'circle' as const,
          symbolSize: (val: number) => (val === maxImp ? 8 : 5),
          lineStyle: { color: strokeColor, width: 1.5, opacity: 0.8 },
          areaStyle: { color: 'rgba(71,85,105,0.06)' },
          itemStyle: { color: '#FFFFFF', borderColor: strokeColor, borderWidth: 1.25 },
          emphasis: {
            itemStyle: { borderColor: '#1E293B', borderWidth: 2 },
            lineStyle: { width: 2 },
          },
          data: [{ value: values, name: 'Competency' }],
          animationDuration: 600,
          animationEasing: 'cubicOut' as const,
          silent: true,
        },
      ],
      animationDuration: 600,
    };
  }, [sorted, maxImp]);

  if (!enabled) return null;

  return (
    <div className="flex w-full items-center justify-center">
      <div ref={containerRef} className="relative w-full max-w-[480px] aspect-square">
        <ReactECharts
          option={option}
          style={{ width: '100%', height: '100%' }}
          opts={{ renderer: 'svg' }}
          notMerge
          lazyUpdate
        />
        {hover && (() => {
          const d = sorted[hover.idx];
          const containerWidth = containerRef.current?.clientWidth ?? 0;
          const offsetX = hover.x > containerWidth / 2 ? 12 : -12;
          const transform = offsetX > 0 ? 'translate(0, -50%)' : 'translate(-100%, -50%)';
          return (
            <div
              className="pointer-events-none absolute z-10 rounded-md bg-ctu-text px-2 py-0.5 text-xs font-semibold text-white"
              style={{
                left: hover.x + offsetX,
                top: hover.y,
                transform,
              }}
            >
              {d.importance.toFixed(2)}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
